import { useId, useState, type KeyboardEvent, type ReactElement } from 'react';

import { ApiError } from '@/lib/api';
import { useDebouncedValue } from '@/lib/use-debounced-value';

import type { PersonSelection } from '../types';
import { usePeopleSearch } from '../use-people';
import { buildItems, type PickerItem } from './person-items';
import { errorAdvice } from '@/lib/error-advice';

interface Props {
  /** Ofrecer "Invitar como proveedor externo" cuando lo escrito es un correo que no aparece. */
  allowInvite: boolean;
  /** Proveedores externos elegibles (false: p. ej. un responsable es de la organización). */
  allowExternal?: boolean;
  /** Personas del LMS elegibles (false: quien no puede agregarlas al QMS). */
  allowLms?: boolean;
  onSelect: (selection: PersonSelection) => void;
}

const forbidden = (error: unknown): boolean => error instanceof ApiError && error.status === 403;

function searchError(error: unknown): string {
  return forbidden(error)
    ? 'No tienes permiso para buscar personas.'
    : `No se pudo buscar. ${errorAdvice(error)}`;
}

function Option({
  item,
  id,
  active,
  onPick,
}: {
  item: PickerItem;
  id: string;
  active: boolean;
  onPick: (s: PersonSelection) => void;
}): ReactElement {
  const cls = [
    'person-opt',
    item.invite && 'person-invite',
    item.selection === null && 'is-disabled',
    active && 'is-active',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div
      id={id}
      role="option"
      aria-selected={active}
      aria-disabled={item.selection === null}
      tabIndex={-1}
      className={cls}
      // mousedown: elegir sin que el campo pierda el foco antes del click.
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => item.selection && onPick(item.selection)}
      onKeyDown={(e) => {
        if (item.selection && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onPick(item.selection);
        }
      }}
    >
      <span className="person-name">{item.label}</span>
      {item.email && <span className="person-mail">{item.email}</span>}
      {item.note && <span className="badge b-mut">{item.note}</span>}
    </div>
  );
}

/** Buscador único de personas (patrón combobox): del QMS (empresa activa), del LMS que
 *  aún no tienen acceso en esta empresa y, si se permite, invitar por correo a un
 *  proveedor externo. Flechas para recorrer, Enter para elegir. */
export function PersonPicker({
  allowInvite,
  allowExternal = true,
  allowLms = true,
  onSelect,
}: Props): ReactElement {
  const baseId = useId();
  const [text, setText] = useState('');
  const [activeIdx, setActiveIdx] = useState(-1);
  // Lista inicial (QA F1 COD-07): con el campo enfocado y vacío se ven las personas del QMS.
  const [browseOpen, setBrowseOpen] = useState(false);
  const q = useDebouncedValue(text.trim(), 300);
  const searching = q.length >= 2;
  const browsing = browseOpen && text.trim() === '' && q === '';
  const search = usePeopleSearch(searching ? q : browsing ? '' : null);
  const ready = searching || browsing;
  const items =
    ready && search.data
      ? buildItems(search.data.results, q, allowInvite, allowExternal, allowLms)
      : [];
  const enabled = items.map((it, i) => (it.selection ? i : -1)).filter((i) => i >= 0);
  const open = items.length > 0;
  const listId = `${baseId}-list`;
  const pick = (selection: PersonSelection): void => {
    setBrowseOpen(false);
    onSelect(selection);
  };
  const optId = (i: number): string => `${baseId}-opt-${i}`;

  function move(step: 1 | -1): void {
    if (enabled.length === 0) {
      return;
    }
    const pos = enabled.indexOf(activeIdx);
    const next =
      pos === -1
        ? step === 1
          ? 0
          : enabled.length - 1
        : (pos + step + enabled.length) % enabled.length;
    setActiveIdx(enabled[next] ?? -1);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      // Patrón combobox: con la lista cerrada, flecha abajo la abre (también tras Escape).
      if (e.key === 'ArrowDown' && !open && text.trim() === '') {
        setBrowseOpen(true);
        return;
      }
      move(e.key === 'ArrowDown' ? 1 : -1);
    } else if (e.key === 'Enter') {
      const sel = items[activeIdx]?.selection;
      if (sel) {
        e.preventDefault();
        pick(sel);
      }
    } else if (e.key === 'Escape') {
      setActiveIdx(-1);
      setBrowseOpen(false);
    }
  }

  const groups = (['qms', 'lms'] as const)
    .map((g) => ({ g, rows: items.map((it, i) => ({ it, i })).filter((r) => r.it.group === g) }))
    .filter((x) => x.rows.length > 0);
  const inviteRow = items.findIndex((it) => it.invite);

  return (
    <div className="fld person-picker">
      <label className="fld-lab" htmlFor={`${baseId}-input`}>
        Persona
      </label>
      <input
        id={`${baseId}-input`}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={activeIdx >= 0 && open ? optId(activeIdx) : undefined}
        className="selector fw"
        value={text}
        maxLength={100}
        autoComplete="off"
        placeholder="Nombre o correo"
        onChange={(e) => {
          setText(e.target.value);
          setActiveIdx(-1);
        }}
        onFocus={() => setBrowseOpen(true)}
        onBlur={() => {
          setBrowseOpen(false);
          setActiveIdx(-1);
        }}
        onKeyDown={onKeyDown}
      />
      {text.trim().length === 1 && (
        <p className="soft person-hint">Escribe al menos 2 caracteres.</p>
      )}
      {/* Lista inicial: la falta de permiso se avisa al escribir; cualquier otra falla sí se dice. */}
      {ready && search.isError && (searching || !forbidden(search.error)) && (
        <p className="soft person-hint" role="alert">
          {searching
            ? searchError(search.error)
            : `No se pudo cargar la lista de personas del QMS. ${errorAdvice(search.error)} También puedes escribir un nombre o correo para buscar.`}
        </p>
      )}
      {ready && !search.data && search.isFetching && (
        <p className="soft person-hint" role="status">
          Buscando…
        </p>
      )}
      {open && (
        <div
          id={listId}
          role="listbox"
          aria-label={browsing ? 'Personas del QMS' : 'Personas encontradas'}
          className="person-list"
          tabIndex={-1}
          // Tocar la barra de scroll o un rótulo no le quita el foco al campo (no la cierra).
          onMouseDown={(e) => e.preventDefault()}
        >
          {groups.map(({ g, rows }) => (
            <div key={g} role="group" aria-labelledby={`${baseId}-${g}`}>
              <div id={`${baseId}-${g}`} className="person-group">
                {g === 'qms' ? 'En el QMS' : 'Del LMS (aún sin acceso en esta empresa)'}
              </div>
              {rows.map(({ it, i }) => (
                <Option
                  key={it.key}
                  item={it}
                  id={optId(i)}
                  active={i === activeIdx}
                  onPick={pick}
                />
              ))}
            </div>
          ))}
          {inviteRow >= 0 && items[inviteRow] && (
            <Option
              item={items[inviteRow]}
              id={optId(inviteRow)}
              active={inviteRow === activeIdx}
              onPick={pick}
            />
          )}
        </div>
      )}
      {browsing && open && (
        <p className="soft person-hint">
          Escribe un nombre o correo para filtrar y buscar también en el LMS.
        </p>
      )}
      {ready && search.data && !open && (
        <p className="soft person-hint" role="status">
          {browsing
            ? 'Aún no hay personas en el QMS de esta empresa. Escribe para buscar en el LMS.'
            : 'Nadie coincide con la búsqueda.'}
        </p>
      )}
      {searching && search.data?.lmsAvailable === false && (
        <p className="soft person-hint">
          No se pudo consultar el LMS: solo se muestran personas que ya están en el QMS.
        </p>
      )}
    </div>
  );
}
